import { Russian } from 'flatpickr/dist/l10n/ru';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { FC } from 'react';
import { useRef } from 'react';
import React from 'react';
import { Props as TextInputProps } from '../TextInput';
import { TextInput } from '../TextInput';
import { dateToString } from './date';
import { stringToDate } from './date';
import { dateTimeToLocalDate } from './date';
import { localDateToDateTime } from './date';
import { useFlatpickr } from './useFlatpickr';
import { useTransformValue } from '../hooks/useTransformValue';
import { Props as UseTransformValue } from '../hooks/useTransformValue';

const defaultFlatpickrOptions = {
    enableTime: false,
    time_24hr: true,
    locale: Russian,
    allowInput: true,
    dateFormat: 'd.m.Y H:i',
    disableMobile: true,
};

export type Props = Omit<TextInputProps, 'value' | 'onChange' | 'className'> &
    Pick<
        UseTransformValue<DateTime | null, DateTime | null>,
        'getValue' | 'setValue'
    > & {
        value?: DateTime | null;
        onChange?: (value: DateTime | null) => void;
        className?: string;
        format?: 'date' | 'datetime' | 'time';
    };

export const DateTimePicker: FC<Props> = ({
    value: originalValue,
    onChange: originalOnChange,
    setValue,
    getValue,
    className,
    format = 'datetime',
    ...props
}) => {
    const nodeRef = useRef<HTMLInputElement>(null);

    const { value, onChange } = useTransformValue({
        value: originalValue,
        onChange: originalOnChange,
        setValue,
        getValue,
    });

    const { luxonFormat, enableTime, noCalendar } = useMemo(() => {
        switch (format) {
            case 'datetime':
                return {
                    luxonFormat: 'dd.LL.yyyy HH:mm',
                    enableTime: true,
                    noCalendar: false,
                };
            case 'date':
                return {
                    luxonFormat: 'dd.LL.yyyy',
                    enableTime: false,
                    noCalendar: false,
                };
            case 'time':
                return {
                    luxonFormat: 'HH:mm',
                    enableTime: true,
                    noCalendar: true,
                };
        }
    }, [format]);

    const parseDate = useCallback(
        (inputValue: string) => stringToDate(inputValue, luxonFormat),
        [luxonFormat]
    );

    const formatDate = useCallback(
        (value: Date | null) => dateToString(value, luxonFormat),
        [luxonFormat]
    );

    useFlatpickr({
        value: useMemo(() => {
            return dateTimeToLocalDate(value || null);
        }, [value]),
        onChange: useCallback(
            (date) => {
                let nextValue = localDateToDateTime(date);
                nextValue = setValue ? setValue(nextValue) : nextValue;
                onChange?.(nextValue);
            },
            [onChange, setValue]
        ),
        nodeRef,
        options: useMemo(
            () => ({
                ...defaultFlatpickrOptions,
                enableTime,
                noCalendar,
            }),
            [enableTime, noCalendar]
        ),
        parseDate,
        formatDate,
    });

    const handleBlur = useCallback(() => {
        if (nodeRef.current) {
            if (value) {
                if (nodeRef.current.value === '') {
                    onChange?.(null);
                } else {
                    nodeRef.current.value = formatDate(
                        dateTimeToLocalDate(value)
                    );
                }
            } else {
                nodeRef.current.value = '';
            }
        }
    }, [formatDate, onChange, value]);

    return (
        <TextInput
            inputRef={nodeRef}
            className={className}
            onBlur={handleBlur}
            {...props}
            type="text"
        />
    );
};
